'use client';

import React, { useState, useRef, useEffect, useCallback } from 'react';
import confetti from 'canvas-confetti';
import { ConfettiSettings } from '@/lib/interfaces/group-card';
import { BiX } from 'react-icons/bi';
import { Sparkles, Check, Palette, Shapes, Play, Wand2, RefreshCw, Star, Heart, Smile, Circle, Square } from 'lucide-react';

interface ConfettiSettingsModalProps {
  isOpen: boolean;
  onClose: () => void;
  initialSettings?: ConfettiSettings;
  onSave: (settings: ConfettiSettings) => void;
  onPreview: (settings: ConfettiSettings) => void;
}

const DEFAULT_CONFETTI_COLORS = ['#ff0000', '#00ff00', '#0000ff', '#ffff00', '#ff00ff'];

const AVAILABLE_COLORS = [
  { name: 'Red', hex: '#ff0000' },
  { name: 'Green', hex: '#00ff00' },
  { name: 'Blue', hex: '#0000ff' },
  { name: 'Yellow', hex: '#ffff00' },
  { name: 'Magenta', hex: '#ff00ff' },
  { name: 'Cyan', hex: '#00ffff' },
  { name: 'Orange', hex: '#ffa500' },
  { name: 'Purple', hex: '#800080' },
  { name: 'Pink', hex: '#ffc0cb' },
  { name: 'Gold', hex: '#ffd700' },
  { name: 'Teal', hex: '#008080' },
  { name: 'White', hex: '#ffffff' },
];

const COLOR_PRESETS = [
  { name: 'Party Rainbow', colors: ['#ff0000', '#00ff00', '#0000ff', '#ffff00', '#ff00ff', '#00ffff'] },
  { name: 'Warm Sunset', colors: ['#ff0000', '#ffa500', '#ffff00', '#ff00ff', '#ffc0cb'] },
  { name: 'Ocean Breeze', colors: ['#0000ff', '#00ffff', '#008080', '#ffffff', '#800080'] },
  { name: 'Golden Glam', colors: ['#ffd700', '#ffa500', '#ffffff', '#ff0000'] },
];

interface StyleOption {
  id: string;
  name: string;
  description: string;
  icon: string;
  badge?: string;
}

const CONFETTI_STYLES: StyleOption[] = [
  { id: 'snow', name: 'Gentle Snow', description: 'Softly falling magical drift', icon: '❄️' },
  { id: 'shower', name: 'Side Shower', description: 'Cascading stream from both sides', icon: '🚿' },
  { id: 'cannon', name: 'Party Cannon', description: 'Upward burst from bottom', icon: '🚀', badge: 'Popular' },
  { id: 'spiral', name: 'Whirlwind Spiral', description: 'Spinning confetti vortex', icon: '🌀' },
  { id: 'burst', name: 'Center Burst', description: 'Instant radial explosion', icon: '💥' },
  { id: 'rain', name: 'Sky Rain', description: 'Continuous shower from above', icon: '🌧️' },
  { id: 'explosion', name: 'Mega Blast', description: 'Multi-directional wave blast', icon: '💣' },
  { id: 'firework', name: 'Sky Fireworks', description: 'Sequential high-flying pops', icon: '🎆' },
];

const SHAPE_CONFIGS = [
  { key: 'circle' as const, label: 'Circle', icon: '🔴', subLabel: 'Classic round dots' },
  { key: 'square' as const, label: 'Square', icon: '🟦', subLabel: 'Standard party flakes' },
  { key: 'star' as const, label: 'Star', icon: '⭐', subLabel: 'Sparkling stars' },
  { key: 'heart' as const, label: 'Heart', icon: '❤️', subLabel: 'Lovely hearts' },
  { key: 'emoji' as const, label: 'Emoji', icon: '🥳', subLabel: 'Fun party emojis' },
];

const ConfettiSheet: React.FC<ConfettiSettingsModalProps> = ({
  isOpen,
  onClose,
  initialSettings,
  onSave,
  onPreview,
}) => {
  const [localSettings, setLocalSettings] = useState<ConfettiSettings>(
    initialSettings || {
      shapes: {
        circle: true,
        square: true,
        star: false,
        heart: false,
        emoji: false,
      },
      colors: DEFAULT_CONFETTI_COLORS,
      style: 'snow',
    }
  );

  const bottomSheetRef = useRef<HTMLDivElement>(null);
  const touchStartY = useRef<number>(0);
  const touchCurrentY = useRef<number>(0);

  // Sync settings when modal opens or initialSettings change
  useEffect(() => {
    if (initialSettings) {
      setLocalSettings(initialSettings);
    }
  }, [initialSettings, isOpen]);

  // Handle ESC key press
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && isOpen) {
        onClose();
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, onClose]);

  // Handle shape toggle
  const toggleShape = (shape: keyof ConfettiSettings['shapes']) => {
    setLocalSettings((prev) => {
      const currentShapes = prev.shapes || { circle: true, square: true, star: false, heart: false, emoji: false };
      const nextShapes = {
        ...currentShapes,
        [shape]: !currentShapes[shape],
      };
      
      // Ensure at least one shape is selected
      const hasAnySelected = Object.values(nextShapes).some(Boolean);
      if (!hasAnySelected) {
        return prev;
      }

      return {
        ...prev,
        shapes: nextShapes,
      };
    });
  };

  const selectAllShapes = () => {
    setLocalSettings((prev) => ({
      ...prev,
      shapes: {
        circle: true,
        square: true,
        star: true,
        heart: true,
        emoji: true,
      },
    }));
  };

  // Handle style change
  const changeStyle = (style: string) => {
    setLocalSettings((prev) => ({
      ...prev,
      style,
    }));
  };

  // Handle color toggle
  const toggleColor = (hex: string) => {
    setLocalSettings((prev) => {
      const currentColors = prev.colors || DEFAULT_CONFETTI_COLORS;
      const newColors = currentColors.includes(hex)
        ? currentColors.filter((c) => c !== hex)
        : [...currentColors, hex];
      
      // Don't allow empty colors
      if (newColors.length === 0) return prev;

      return {
        ...prev,
        colors: newColors,
      };
    });
  };

  const applyColorPreset = (colors: string[]) => {
    setLocalSettings((prev) => ({
      ...prev,
      colors,
    }));
  };

  // Save settings
  const saveSettings = () => {
    onSave(localSettings);
    onClose();
  };

  const getShape = (shape: string): any => {
    switch (shape) {
      case 'square':
        return 'square';
      case 'star':
        return 'star';
      case 'heart':
        return confetti.shapeFromText({ text: '❤️', scalar: 2 });
      case 'emoji':
        return confetti.shapeFromText({ text: '🥳', scalar: 2 });
      default:
        return 'circle';
    }
  };

  const randomInRange = (min: number, max: number) => Math.random() * (max - min) + min;

  // Animation implementations
  const playSnow = (colors: string[], shapes: any[]) => {
    const duration = 3500;
    let animationEnd = Date.now() + duration;
    let skew = 1;

    (function frame() {
      const timeLeft = animationEnd - Date.now();
      const ticks = Math.max(200, 500 * (timeLeft / duration));
      skew = Math.max(0.8, skew - 0.001);

      confetti({
        particleCount: 2,
        origin: { x: Math.random(), y: Math.random() * skew - 0.2 },
        colors: [colors[Math.floor(Math.random() * colors.length)]],
        shapes,
        ticks,
        startVelocity: 0,
        gravity: randomInRange(0.4, 0.6),
        scalar: randomInRange(0.5, 1.2),
        drift: randomInRange(-0.4, 0.4),
        zIndex: 2000,
        disableForReducedMotion: true,
      });

      if (Date.now() < animationEnd) {
        requestAnimationFrame(frame);
      }
    })();
  };

  const playShower = (colors: string[], shapes: any[]) => {
    const options: confetti.Options = {
      particleCount: 3,
      spread: 60,
      colors,
      shapes,
      ticks: 350,
      zIndex: 2000,
      disableForReducedMotion: true,
      scalar: randomInRange(1, 1.8),
    };

    const end = Date.now() + 2500;
    (function frame() {
      confetti({ ...options, angle: 60, origin: { x: 0, y: 0.3 } });
      confetti({ ...options, angle: 120, origin: { x: 1, y: 0.3 } });
      if (Date.now() < end) requestAnimationFrame(frame);
    })();
  };

  const playCannon = (colors: string[], shapes: any[], particleCount: number) => {
    confetti({
      particleCount: particleCount * 1.2,
      angle: 90,
      spread: 55,
      startVelocity: 65,
      decay: 0.9,
      gravity: 1,
      drift: 0,
      ticks: 300,
      colors,
      shapes,
      origin: { x: 0.5, y: 0.75 },
      zIndex: 2000,
      disableForReducedMotion: true,
      scalar: randomInRange(1, 1.8),
    });
  };

  const playSpiral = (colors: string[], shapes: any[], particleCount: number) => {
    let angle = 0;
    const duration = 2500;
    const end = Date.now() + duration;

    (function frame() {
      confetti({
        particleCount: Math.max(2, Math.floor(particleCount / 40)),
        angle,
        spread: 90,
        origin: { x: 0.5, y: 0.5 },
        colors,
        shapes,
        ticks: 250,
        startVelocity: 30,
        zIndex: 2000,
        disableForReducedMotion: true,
        scalar: randomInRange(1, 1.5),
      });

      angle += 20;
      if (Date.now() < end) requestAnimationFrame(frame);
    })();
  };

  const playBurst = (colors: string[], shapes: any[], particleCount: number) => {
    confetti({
      particleCount: particleCount * 1.1,
      spread: 120,
      colors,
      shapes,
      origin: { x: 0.5, y: 0.5 },
      zIndex: 2000,
      disableForReducedMotion: true,
      scalar: randomInRange(1, 1.8),
    });
  };

  const playRain = (colors: string[], shapes: any[]) => {
    const duration = 2500;
    const end = Date.now() + duration;
    (function frame() {
      confetti({
        particleCount: 2,
        startVelocity: 0,
        ticks: 350,
        gravity: 0.6,
        origin: { x: Math.random(), y: 0 },
        colors,
        shapes,
        zIndex: 2000,
        disableForReducedMotion: true,
        scalar: randomInRange(0.8, 1.5),
      });

      if (Date.now() < end) requestAnimationFrame(frame);
    })();
  };

  const playExplosion = (colors: string[], shapes: any[], particleCount: number) => {
    for (let i = 0; i < 4; i++) {
      setTimeout(() => {
        confetti({
          particleCount: Math.floor(particleCount / 2),
          spread: 360,
          startVelocity: 50,
          angle: Math.random() * 360,
          origin: { x: randomInRange(0.3, 0.7), y: randomInRange(0.3, 0.7) },
          colors,
          shapes,
          zIndex: 2000,
          disableForReducedMotion: true,
          scalar: randomInRange(1, 1.8),
        });
      }, i * 200);
    }
  };

  const playFirework = (colors: string[], shapes: any[]) => {
    const duration = 4000;
    const animationEnd = Date.now() + duration;
    const interval = setInterval(() => {
      const timeLeft = animationEnd - Date.now();
      confetti({
        particleCount: Math.floor(40 * (timeLeft / duration)),
        spread: 360,
        startVelocity: 55,
        angle: Math.random() * 360,
        origin: { x: randomInRange(0.2, 0.8), y: randomInRange(0.2, 0.6) },
        colors,
        shapes,
        zIndex: 2000,
        disableForReducedMotion: true,
        scalar: randomInRange(1, 1.8),
      });
    }, 450);

    setTimeout(() => {
      clearInterval(interval);
    }, duration);
  };

  const triggerConfettiAnimation = useCallback((settingsToUse = localSettings) => {
    let shapes: any[] = ['circle'];
    if (settingsToUse.shapes && typeof settingsToUse.shapes === 'object') {
      const selectedShapes = Object.entries(settingsToUse.shapes)
        .filter(([_, enabled]) => enabled)
        .map(([shape]) => getShape(shape));

      if (selectedShapes.length > 0) {
        shapes = selectedShapes;
      }
    }

    const colors =
      settingsToUse.colors && Array.isArray(settingsToUse.colors) && settingsToUse.colors.length > 0
        ? settingsToUse.colors
        : DEFAULT_CONFETTI_COLORS;

    const particleCount = 100;

    switch (settingsToUse.style) {
      case 'shower':
        playShower(colors, shapes);
        break;
      case 'cannon':
        playCannon(colors, shapes, particleCount);
        break;
      case 'spiral':
        playSpiral(colors, shapes, particleCount);
        break;
      case 'burst':
        playBurst(colors, shapes, particleCount);
        break;
      case 'rain':
        playRain(colors, shapes);
        break;
      case 'explosion':
        playExplosion(colors, shapes, particleCount);
        break;
      case 'firework':
        playFirework(colors, shapes);
        break;
      default:
        playSnow(colors, shapes);
    }
  }, [localSettings]);

  const handlePreview = () => {
    onPreview(localSettings);
    triggerConfettiAnimation(localSettings);
  };

  const handleSelectStyleWithPreview = (styleId: string) => {
    const updated = { ...localSettings, style: styleId };
    setLocalSettings(updated);
    triggerConfettiAnimation(updated);
  };

  const onTouchStart = (e: React.TouchEvent) => {
    touchStartY.current = e.touches[0].clientY;
    touchCurrentY.current = touchStartY.current;
  };

  const onTouchMove = (e: React.TouchEvent) => {
    touchCurrentY.current = e.touches[0].clientY;
    const deltaY = touchCurrentY.current - touchStartY.current;
    if (deltaY > 0 && bottomSheetRef.current) {
      bottomSheetRef.current.style.transform = `translateY(${deltaY}px)`;
    }
  };

  const onTouchEnd = () => {
    if (!bottomSheetRef.current) return;
    const deltaY = touchCurrentY.current - touchStartY.current;
    if (deltaY > 120) {
      onClose();
    }
    bottomSheetRef.current.style.transform = 'translateY(0)';
  };

  if (!isOpen) return null;

  return (
    <>
      {/* Backdrop */}
      <div
        className="fixed inset-0 bg-slate-950/60 backdrop-blur-sm z-50 transition-opacity duration-300 animate-in fade-in"
        onClick={onClose}
        aria-hidden="true"
      />

      {/* Sheet Container */}
      <div
        ref={bottomSheetRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby="confetti-sheet-title"
        className="fixed bottom-0 left-0 right-0 z-[51] max-h-[85vh] sm:max-h-[80vh] lg:w-[780px] md:w-[92%] sm:w-full mx-auto overflow-hidden rounded-t-[2.5rem] bg-white/95 dark:bg-slate-900/95 backdrop-blur-2xl shadow-2xl border-t border-x border-white/20 dark:border-slate-800 flex flex-col transition-all duration-300 transform translate-y-0"
        onTouchStart={onTouchStart}
        onTouchMove={onTouchMove}
        onTouchEnd={onTouchEnd}
      >
        {/* Top Drag Handle */}
        <div className="w-full pt-3 pb-1 flex justify-center cursor-grab active:cursor-grabbing">
          <div className="w-12 h-1.5 bg-gray-300 dark:bg-slate-700 rounded-full hover:bg-gray-400 transition-colors" />
        </div>

        {/* Modal Header */}
        <div className="px-6 py-4 flex items-center justify-between border-b border-gray-100 dark:border-slate-800/80">
          <div className="flex items-center space-x-3">
            <div className="p-2.5 rounded-2xl bg-primary-500 text-white shadow-md shadow-pink-500/20">
              <Sparkles className="w-5 h-5 animate-pulse" />
            </div>
            <div>
              <h2 id="confetti-sheet-title" className="text-lg font-bold text-slate-900 dark:text-white flex items-center gap-2">
                Confetti Effects
               
              </h2>
              <p className="text-xs text-gray-500 dark:text-slate-400">
                Personalize shapes, colors, and animation styles
              </p>
            </div>
          </div>

          <button
            type="button"
            onClick={onClose}
            aria-label="Close modal"
            className="p-2 rounded-full text-gray-400 hover:text-slate-700 dark:hover:text-white hover:bg-gray-100 dark:hover:bg-slate-800 transition-all duration-200"
          >
            <BiX size={26} />
          </button>
        </div>

        {/* Modal Body - Scrollable Content */}
        <div className="p-6 overflow-y-auto space-y-7 [&::-webkit-scrollbar]:w-1.5 [&::-webkit-scrollbar-thumb]:rounded-full [&::-webkit-scrollbar-thumb]:bg-gray-300 dark:[&::-webkit-scrollbar-thumb]:bg-slate-700">
          
          {/* SECTION 1: SHAPES */}
          <div>
            <div className="flex items-center justify-between mb-3.5">
              <label className="text-xs font-bold uppercase tracking-wider text-slate-500 dark:text-slate-400 flex items-center gap-1.5">
                <Shapes className="w-4 h-4 text-purple-500" />
                Confetti Shapes
              </label>
              <button
                type="button"
                onClick={selectAllShapes}
                className="text-xs font-semibold text-purple-600 dark:text-purple-400 hover:underline"
              >
                Select All
              </button>
            </div>

            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 gap-3">
              {SHAPE_CONFIGS.map((shape) => {
                const isSelected = !!localSettings.shapes?.[shape.key];
                return (
                  <button
                    key={shape.key}
                    type="button"
                    onClick={() => toggleShape(shape.key)}
                    className={`relative p-3.5 rounded-2xl border text-left transition-all duration-200 flex flex-col items-center justify-center space-y-2 group cursor-pointer ${
                      isSelected
                        ? 'border-purple-500 bg-purple-50/60 dark:bg-purple-950/30 ring-2 ring-purple-500/20 shadow-sm'
                        : 'border-gray-200 dark:border-slate-800 bg-gray-50/50 dark:bg-slate-800/40 hover:border-gray-300 dark:hover:border-slate-700'
                    }`}
                  >
                    {/* Active check badge */}
                    <div
                      className={`absolute top-2 right-2 w-4 h-4 rounded-full flex items-center justify-center transition-all ${
                        isSelected
                          ? 'bg-purple-600 text-white scale-100'
                          : 'border border-gray-300 dark:border-slate-600 bg-white dark:bg-slate-700 opacity-40 scale-90'
                      }`}
                    >
                      {isSelected && <Check className="w-2.5 h-2.5 stroke-[3]" />}
                    </div>

                    <span className="text-2xl transform group-hover:scale-110 transition-transform duration-200">
                      {shape.icon}
                    </span>
                    <span
                      className={`text-xs font-medium text-center ${
                        isSelected ? 'text-purple-950 dark:text-purple-200 font-semibold' : 'text-gray-700 dark:text-slate-300'
                      }`}
                    >
                      {shape.label}
                    </span>
                  </button>
                );
              })}
            </div>
          </div>

          {/* SECTION 2: COLOR PALETTE */}
          <div>
            <div className="flex items-center justify-between mb-3">
              <label className="text-xs font-bold uppercase tracking-wider text-slate-500 dark:text-slate-400 flex items-center gap-1.5">
                <Palette className="w-4 h-4 text-pink-500" />
                Color Palette
              </label>
              <span className="text-xs text-gray-400 dark:text-slate-500 font-medium">
                {localSettings.colors?.length || 0} selected
              </span>
            </div>

            {/* Presets bar */}
            <div className="flex items-center gap-2 overflow-x-auto pb-2.5 mb-3.5 scrollbar-none">
              <span className="text-[11px] font-semibold text-gray-400 uppercase tracking-wider shrink-0">Presets:</span>
              {COLOR_PRESETS.map((preset) => (
                <button
                  key={preset.name}
                  type="button"
                  onClick={() => applyColorPreset(preset.colors)}
                  className="px-2.5 py-1 text-xs rounded-full border border-gray-200 dark:border-slate-700 bg-white dark:bg-slate-800 text-gray-700 dark:text-slate-300 hover:bg-gray-100 dark:hover:bg-slate-700 font-medium whitespace-nowrap transition-colors flex items-center gap-1.5 shadow-2xs"
                >
                  <div className="flex -space-x-1">
                    {preset.colors.slice(0, 3).map((c, i) => (
                      <span key={i} className="w-2.5 h-2.5 rounded-full border border-white dark:border-slate-800" style={{ backgroundColor: c }} />
                    ))}
                  </div>
                  {preset.name}
                </button>
              ))}
            </div>

            {/* Color Swatches Grid */}
            <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-6 gap-2.5">
              {AVAILABLE_COLORS.map((color) => {
                const isChecked = localSettings.colors?.includes(color.hex);
                return (
                  <button
                    key={color.hex}
                    type="button"
                    onClick={() => toggleColor(color.hex)}
                    className={`flex items-center space-x-2.5 p-2 rounded-xl border transition-all duration-200 group text-left cursor-pointer ${
                      isChecked
                        ? 'border-slate-900 dark:border-white bg-slate-50 dark:bg-slate-800/80 shadow-xs'
                        : 'border-gray-200 dark:border-slate-800 bg-gray-50/40 dark:bg-slate-800/30 hover:border-gray-300 dark:hover:border-slate-700'
                    }`}
                  >
                    <div
                      className="w-5 h-5 rounded-full flex items-center justify-center shrink-0 border border-black/10 shadow-xs transition-transform group-hover:scale-110"
                      style={{ backgroundColor: color.hex }}
                    >
                      {isChecked && (
                        <Check
                          className={`w-3 h-3 ${
                            color.hex.toLowerCase() === '#ffffff' || color.hex.toLowerCase() === '#ffff00'
                              ? 'text-black'
                              : 'text-white'
                          }`}
                        />
                      )}
                    </div>
                    <span className="text-xs font-medium text-slate-800 dark:text-slate-200 truncate">
                      {color.name}
                    </span>
                  </button>
                );
              })}
            </div>
          </div>

          {/* SECTION 3: ANIMATION STYLES */}
          <div>
            <div className="flex items-center justify-between mb-3.5">
              <label className="text-xs font-bold uppercase tracking-wider text-slate-500 dark:text-slate-400 flex items-center gap-1.5">
                <Wand2 className="w-4 h-4 text-amber-500" />
                Animation Style
              </label>
              <span className="text-xs text-gray-400 dark:text-slate-500">
                Click any style to test
              </span>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {CONFETTI_STYLES.map((style) => {
                const isActive = localSettings.style === style.id;
                return (
                  <button
                    key={style.id}
                    type="button"
                    onClick={() => handleSelectStyleWithPreview(style.id)}
                    className={`relative p-3.5 rounded-2xl border text-left transition-all duration-200 flex items-center justify-between group cursor-pointer ${
                      isActive
                        ? 'border-amber-500 bg-amber-50/50 dark:bg-amber-950/20 ring-2 ring-amber-500/20 shadow-sm'
                        : 'border-gray-200 dark:border-slate-800 bg-gray-50/50 dark:bg-slate-800/40 hover:border-gray-300 dark:hover:border-slate-700'
                    }`}
                  >
                    <div className="flex items-center space-x-3.5 min-w-0">
                      <div
                        className={`w-10 h-10 rounded-xl flex items-center justify-center text-xl shrink-0 transition-transform group-hover:scale-110 ${
                          isActive
                            ? 'bg-amber-500 text-white shadow-md shadow-amber-500/30'
                            : 'bg-white dark:bg-slate-700 text-slate-700 dark:text-slate-200 border border-gray-200 dark:border-slate-600'
                        }`}
                      >
                        {style.icon}
                      </div>
                      <div className="min-w-0">
                        <div className="flex items-center gap-2">
                          <h4
                            className={`text-xs font-bold truncate ${
                              isActive ? 'text-amber-950 dark:text-amber-200' : 'text-slate-900 dark:text-white'
                            }`}
                          >
                            {style.name}
                          </h4>
                          {style.badge && (
                            <span className="px-1.5 py-0.2 rounded text-[9px] font-extrabold uppercase tracking-wide bg-gradient-to-r from-amber-500 to-red-500 text-white">
                              {style.badge}
                            </span>
                          )}
                        </div>
                        <p className="text-[11px] text-gray-500 dark:text-slate-400 truncate mt-0.5">
                          {style.description}
                        </p>
                      </div>
                    </div>

                    <div
                      className={`w-5 h-5 rounded-full flex items-center justify-center shrink-0 ml-2 transition-all ${
                        isActive
                          ? 'bg-amber-500 text-white scale-100'
                          : 'border border-gray-300 dark:border-slate-600 opacity-40 scale-90'
                      }`}
                    >
                      {isActive && <Check className="w-3 h-3 stroke-[3]" />}
                    </div>
                  </button>
                );
              })}
            </div>
          </div>
        </div>

        {/* Modal Footer */}
        <div className="flex items-center justify-between gap-2 border-t border-paper-rule bg-white px-4 py-3 sm:px-6">
          <button
            type="button"
            onClick={handlePreview}
            className="inline-flex h-10 items-center gap-2 rounded-full border border-paper-rule bg-white px-4 text-sm font-semibold text-ink transition hover:bg-paper"
          >
            <Play className="h-3.5 w-3.5 fill-current text-heart-600" />
            <span className="hidden sm:inline">Preview effect</span>
            <span className="sm:hidden">Preview</span>
          </button>

          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={onClose}
              className="inline-flex h-10 items-center rounded-full border border-paper-rule bg-white px-4 text-sm font-semibold text-ink transition hover:bg-paper"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={saveSettings}
              className="inline-flex h-10 items-center gap-1.5 rounded-full bg-ink px-5 text-sm font-semibold text-white transition hover:bg-ink-700"
            >
              <Check className="h-4 w-4 stroke-[2.5]" />
              Save settings
            </button>
          </div>
        </div>
      </div>
    </>
  );
};

export default ConfettiSheet;